import React, { memo, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsContext } from '../../../../../config/GoogleTagManagerEvents';
import { useGuidedRegContext } from '../../../context/guidedRegContext';
import useNumberVerification from './useNumberVerification';
import { LoadingIndicator } from '../../../../../aem-core-components';
import Button from '../../../../global/atoms/button';
import OtpInput from './OtpInput';
import JoinCashVerificationFailedModal from '../verificationFailedModal';
import JoinCashVerificationTimeoutModal from '../verificationTimeoutModal';
import { grPersonalInfoShimmerEffect } from '../../../utils/shimmerUtil';
import { getJoinAccountData, navigateToPath } from '../../utils/cashflowUtils';
import AlertSuccessCircle from '../../../../../resources/images/checkmark-circle-2.svg';
import { SIX, STEP_NAMES } from '../../constants';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../constants/analyticsConstants/Ecommerce';
import './numberVerification.scss';

const NumberVerification = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [otp, setOtp] = useState('');
    const { screenConfig = {} } = getJoinAccountData() || {};
    const [{ formData }] = useGuidedRegContext();
    const { mobileNumber } = formData[STEP_NAMES[0]]?.data || '';
    const mobNo = mobileNumber ? '+1' + mobileNumber.replace(/-/g, '') : '';
    const {
        joinAccountFeatureDescription = '',
        errorToast = '',
        disclaimerNotificationSubText = '',
        joinAccountPageTitle = '',
        joinAccountFormButtonLabel = '',
        disclaimerText = '',
        disclaimerSubText = '',
        extendedDisclaimerText = ''
    } = screenConfig || {};
    const { title = '', description = '' } = errorToast || {};
    const {
        failedModal,
        timeoutModal,
        showOtpSendBanner,
        isLoading,
        errorMessage,
        setFailedModal = () => {},
        setTimeoutModal = () => {},
        validateVerificationCode = () => {},
        setIsLoading = () => {},
        setErrorMessage = () => {},
        handleResendCode = () => {},
        onInputFieldFocus = () => {}
    } = useNumberVerification();

    const { sendEventsForEmailVerifyFormSubmitted, sendEventsForPageViewed, sendEventsForModalClosed } =
        useAnalyticsContext();

    useEffect(() => {
        window.scrollTo(0, 0);
        if (formData[STEP_NAMES[0]]?.data?.firstName == '') {
            // data not exists navigate to cashflow page
            navigateToPath(navigate, '');
        }
    }, []);

    useEffect(() => {
        grPersonalInfoShimmerEffect();
        sendEventsForPageViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PAGE_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
            window.location.href
        );
    }, []);

    const hideFailedModal = () => {
        setFailedModal(false);
        sendCompletedAnalytics();
    };

    const hideTimeoutModal = () => {
        setTimeoutModal(false);
        sendCompletedAnalytics();
    };

    const sendCompletedAnalytics = () => {
        sendEventsForModalClosed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_CLOSE,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_BUTTON,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_MODAL,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE
        );
    };

    const handleSubmit = event => {
        event.preventDefault();
    };

    const onOtpSubmit = otp => {
        setErrorMessage('');
        setOtp(otp);
    };

    const onContinue = async () => {
        sendEventsForEmailVerifyFormSubmitted(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_SUBMITTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_JOIN_CASH_FORM_NAME,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PERSONAL
        );
        if (otp.length == SIX) {
            // continue call the api
            setIsLoading(true);
            await validateVerificationCode(mobNo, otp);
        } else {
            setErrorMessage(intl.formatMessage({ id: 'verify_code' }));
        }
    };

    const handleHtmlData = htmlData => {
        return (
            <span
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(htmlData)
                }}
            />
        );
    };

    const renderResendOtpBanner = () => {
        return (
            <div className="verify-otp__resend-otp-view" data-testid="resendOtpBanner">
                <AlertSuccessCircle />
                <span className="verify-otp__resend-otp-view-text">{disclaimerNotificationSubText}</span>
            </div>
        );
    };

    return (
        <div className={isLoading ? 'loading_overlay_background' : ''}>
            <section className="verify-otp__section">
                <div className="container">
                    <div className="row verify-otp__row">
                        <div className="col-lg-6">
                            <div className="form-heading verify-otp__heading">
                                <h2 className="verify-otp__title">{joinAccountPageTitle}</h2>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <fieldset className="verify-otp__fieldset">
                                    <p className="verify-otp__legend">
                                        {handleHtmlData(joinAccountFeatureDescription)}
                                    </p>

                                    <div>
                                        <OtpInput
                                            otpError={errorMessage}
                                            onOtpSubmit={onOtpSubmit}
                                            onInputFieldFocus={onInputFieldFocus}></OtpInput>
                                        <div>
                                            <small className={`verify-otp__info_text`}>
                                                {disclaimerText}
                                                <a
                                                    href="#"
                                                    className="verify-otp__link"
                                                    onClick={() => handleResendCode(mobNo)}>
                                                    {disclaimerSubText}
                                                </a>
                                            </small>
                                        </div>
                                        <div>
                                            <small className={`verify-otp__info_text`}>
                                                {handleHtmlData(extendedDisclaimerText)}
                                            </small>
                                        </div>
                                    </div>
                                </fieldset>
                                {showOtpSendBanner ? renderResendOtpBanner() : null}
                                <Button
                                    className="button button-primary button-block"
                                    type="button"
                                    onClick={onContinue}
                                    dataTestid={'continue_button'}
                                    buttonAriaLabel={joinAccountFormButtonLabel}>
                                    {joinAccountFormButtonLabel}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
                {failedModal && (
                    <JoinCashVerificationFailedModal failedModal={failedModal} hideFailedModal={hideFailedModal} />
                )}
                {timeoutModal && (
                    <JoinCashVerificationTimeoutModal
                        timeoutModal={timeoutModal}
                        hideTimeoutModal={hideTimeoutModal}
                        resendCode={() => handleResendCode(mobNo)}
                    />
                )}
            </section>
            {isLoading && (
                <div className="globalmodal__overlay loading_overlay">
                    <LoadingIndicator />
                </div>
            )}
        </div>
    );
};

export default memo(NumberVerification);
